/**
 * "You may also like" tabs.
 * Each tab has a Swiper carousel built like the Recently Viewed section
 * (same slides per view, spacing, hover arrows and edge fade).
 * A carousel starts when its tab is first shown, so it measures correctly.
 */
class RecommendationTabs extends HTMLElement {
  connectedCallback() {
    const tabs = this.querySelectorAll('[data-recommendation-tab]');
    const panels = this.querySelectorAll('[data-recommendation-panel]');

    if (!tabs.length || !panels.length) return;

    // Hide tabs whose panel has no products, so customers never land on an empty tab.
    /** @type {HTMLElement | null} */
    let firstVisibleTab = null;
    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const panel = this.querySelector(`[data-recommendation-panel="${tab.dataset.recommendationTab}"]`);
      const isEmpty = !panel || !panel.querySelector('[data-has-recommendations="true"]');
      tab.hidden = isEmpty;
      tab.style.display = isEmpty ? 'none' : '';
      if (!isEmpty && !firstVisibleTab) firstVisibleTab = tab;
    });

    if (!firstVisibleTab) {
      const section = this.closest('.product-recommendations');
      if (section instanceof HTMLElement) section.hidden = true;
      return;
    }

    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      tab.addEventListener('click', () => this.#activate(tab.dataset.recommendationTab));
    });

    this.#activate(firstVisibleTab.dataset.recommendationTab);
  }

  /**
   * @param {string | undefined} tabId
   */
  #activate(tabId) {
    if (!tabId) return;

    this.querySelectorAll('[data-recommendation-tab]').forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const isActive = tab.dataset.recommendationTab === tabId;
      tab.classList.toggle('recommendation-tabs__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    /** @type {HTMLElement | null} */
    let activePanel = null;
    this.querySelectorAll('[data-recommendation-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.dataset.recommendationPanel === tabId;
      panel.classList.toggle('recommendation-tabs__panel--active', isActive);
      panel.hidden = !isActive;
      if (isActive) activePanel = panel;
    });

    if (activePanel) requestAnimationFrame(() => this.#initSlider(activePanel));
  }

  /**
   * Same set-up as the Recently Viewed slider: prev/next arrows, then initialize.
   * @param {HTMLElement} panel
   */
  #initSlider(panel) {
    const slider = panel.querySelector('.js-recommendation-tabs__slider');
    if (!slider) return;

    // Already running: just re-measure (it may have been hidden)
    if (slider.swiper) {
      slider.swiper.update();
      return;
    }

    const prev = panel.querySelector('.js-recommendation-tabs__arrow-prev');
    const next = panel.querySelector('.js-recommendation-tabs__arrow-next');
    if (prev && next) {
      Object.assign(slider, { navigation: { prevEl: prev, nextEl: next } });
    }

    if (typeof slider.initialize === 'function') {
      slider.initialize();
    } else {
      // Swiper not loaded yet: try again once it is
      customElements.whenDefined('swiper-container').then(() => {
        if (!slider.swiper && typeof slider.initialize === 'function') slider.initialize();
      });
    }
  }
}

if (!customElements.get('recommendation-tabs')) {
  customElements.define('recommendation-tabs', RecommendationTabs);
}
